// Notification store for friend/party/trade realtime events pushed by gameserver.
import { create } from 'zustand';
import { getPitayaClient } from '@/network/PitayaClient';
import { useUIStore } from './uiStore';
import { useCharacterStore } from './characterStore';
import { useTradeStore, openTradePanel } from './tradeStore';
import { inventory } from '@/systems/InventorySystem';
import { Character } from '@/network/RestApi';

export interface FriendRequestEvent {
  requestId: number;
  fromId: number;
  fromName: string;
}
export interface FriendAcceptedEvent {
  friendId: number;
  friendName: string;
}
export interface PartyInvitedEvent {
  partyId: number;
  leaderId: number;
  leaderName: string;
}
export interface PartyKickedEvent {
  partyId: number;
}

export interface PartyExpSharedEvent {
  fromName: string;
  expGained: number;
  newExp: number;
  newLevel: number;
  leveledUp: boolean;
  statPoints?: number;
  skillPoints?: number;
}

interface NotificationState {
  friendRequests: number;
  partyInvites: number;
  refreshTickFriend: number;
  refreshTickParty: number;
  setFriendRequests: (n: number) => void;
  setPartyInvites: (n: number) => void;
  bumpFriend: () => void;
  bumpParty: () => void;
  clearFriend: () => void;
  clearParty: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  friendRequests: 0,
  partyInvites: 0,
  refreshTickFriend: 0,
  refreshTickParty: 0,
  setFriendRequests: (n) => set({ friendRequests: Math.max(0, n) }),
  setPartyInvites: (n) => set({ partyInvites: Math.max(0, n) }),
  bumpFriend: () => set((s) => ({
    friendRequests: s.friendRequests + 1,
    refreshTickFriend: s.refreshTickFriend + 1,
  })),
  bumpParty: () => set((s) => ({
    partyInvites: s.partyInvites + 1,
    refreshTickParty: s.refreshTickParty + 1,
  })),
  clearFriend: () => set((s) => ({ friendRequests: 0, refreshTickFriend: s.refreshTickFriend + 1 })),
  clearParty: () => set((s) => ({ partyInvites: 0, refreshTickParty: s.refreshTickParty + 1 })),
}));

let attached = false;
const unsubs: Array<() => void> = [];

export function attachNotificationListeners() {
  if (attached) return;
  const cli = getPitayaClient();
  if (!cli) return;
  attached = true;
  const showToast = useUIStore.getState().showToast;

  unsubs.push(cli.on('friend_request_received', (raw) => {
    const ev = raw as FriendRequestEvent;
    showToast(`Lời mời kết bạn từ ${ev.fromName ?? '???'}`, 'info');
    useNotificationStore.getState().bumpFriend();
  }));

  unsubs.push(cli.on('friend_request_accepted', (raw) => {
    const ev = raw as FriendAcceptedEvent;
    showToast(`${ev.friendName ?? '???'} đã chấp nhận lời mời`, 'info');
    useNotificationStore.getState().bumpFriend();
  }));

  unsubs.push(cli.on('party_invited', (raw) => {
    const ev = raw as PartyInvitedEvent;
    showToast(`${ev.leaderName ?? '???'} mời bạn vào party`, 'info');
    useNotificationStore.getState().bumpParty();
  }));

  unsubs.push(cli.on('party_kicked', (_raw) => {
    showToast('Bạn đã bị đuổi khỏi party', 'warn');
    useNotificationStore.getState().bumpParty();
  }));

  unsubs.push(cli.on('party_exp_shared', (raw) => {
    const ev = raw as PartyExpSharedEvent;
    if (!ev || !ev.expGained) return;
    useCharacterStore.getState().applyExpGain({
      exp: ev.newExp,
      level: ev.newLevel,
      statPoints: ev.statPoints,
      skillPoints: ev.skillPoints,
    });
    showToast(`+${ev.expGained} EXP (party · ${ev.fromName})`, 'info');
    if (ev.leveledUp) showToast(`Level Up! Lv.${ev.newLevel}`, 'info');
  }));

  unsubs.push(cli.on('trade_request_received', (raw) => {
    const ev = raw as { requestId: number; fromId: number; fromName: string };
    if (!ev || !ev.requestId) return;
    useTradeStore.getState().pushIncoming({
      requestId: ev.requestId,
      fromId: ev.fromId,
      fromName: ev.fromName ?? '???',
    });
    showToast(`${ev.fromName ?? '???'} muốn giao dịch với bạn`, 'info');
    openTradePanel();
  }));

  unsubs.push(cli.on('trade_started', (raw) => {
    const ev = raw as { tradeId: number; otherName: string };
    showToast(`Bắt đầu giao dịch với ${ev?.otherName ?? '???'}`, 'info');
    useTradeStore.getState().refresh();
    openTradePanel();
  }));

  unsubs.push(cli.on('trade_updated', (_raw) => {
    useTradeStore.getState().refresh();
  }));

  unsubs.push(cli.on('trade_cancelled', (_raw) => {
    showToast('Phiên giao dịch đã bị huỷ', 'warn');
    useTradeStore.getState().clearSession();
    useUIStore.getState().set('trade', false);
  }));

  unsubs.push(cli.on('trade_completed', (_raw) => {
    showToast('Giao dịch hoàn tất', 'info');
    useTradeStore.getState().clearSession();
    useUIStore.getState().set('trade', false);
    // Refresh inventory + character (zen) so UI reflects swapped items.
    inventory.refresh().catch(() => { /* ignore */ });
    Character.list()
      .then((r) => {
        const cur = useCharacterStore.getState().current;
        if (!cur) return;
        const updated = r.characters.find((c) => c.id === cur.id);
        if (updated) useCharacterStore.getState().setCurrent(updated);
      })
      .catch(() => { /* ignore */ });
  }));
}

export function detachNotificationListeners() {
  attached = false;
  for (const u of unsubs) try { u(); } catch { /* ignore */ }
  unsubs.length = 0;
}
